'use client';

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, type PropsWithChildren } from 'react';

export type TransferType = 'priority' | 'regular';

export interface SelectedItem {
  product_id: number;
  sku: string;
  item_name: string;
  thumbnail?: string;
  dailyAvg: number;
  available: number;
  rec_qty: number;
  allocated_qty: number;
  variations?: string[];
  sources: {
    label: string;
    available: number;
    bins: {
      name: string;
      available: number;
    }[];
  }[];
}

interface TransferContextType {
  selectedItems: Map<string, SelectedItem>;
  transferType: TransferType;
  isHydrated: boolean;
  setTransferType: (type: TransferType) => void;
  addItem: (item: SelectedItem) => void;
  removeItem: (sku: string) => void;
  clearSelection: () => void;
}

const TransferContext = createContext<TransferContextType | undefined>(undefined);

export const TransferProvider = ({ children }: PropsWithChildren) => {
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedItem>>(new Map());
  const [transferType, setTransferType] = useState<TransferType>('priority');
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('saefulaskar-ops-transfer-selection');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.items) {
          const items = Object.entries(parsed.items) as [string, SelectedItem][];
          // Migrate old items that don't have sources
          const migratedItems = items.map(([sku, item]) => {
            return [
              sku,
              {
                ...item,
                thumbnail: item.thumbnail ?? '',
                dailyAvg: item.dailyAvg ?? 0,
                sources: (item.sources ?? []).map(s => ({
                  ...s,
                  bins: (s as any).bins ?? [{ name: (s as any).bin_name ?? '', available: s.available }],
                })),
              },
            ] as [string, SelectedItem];
          });
          setSelectedItems(new Map(migratedItems));
        }
        if (parsed.type) {
          setTransferType(parsed.type);
        }
      } catch (e) {
        console.error('Failed to parse transfer selection', e);
      }
    }
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      localStorage.setItem('saefulaskar-ops-transfer-selection', JSON.stringify({
        items: Object.fromEntries(selectedItems),
        type: transferType
      }));
    }
  }, [selectedItems, transferType, isHydrated]);

  const addItem = useCallback((item: SelectedItem) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      next.set(item.sku, item);
      return next;
    });
  }, []);

  const removeItem = useCallback((sku: string) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      next.delete(sku);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedItems(new Map()), []);

  const value: TransferContextType = useMemo(() => ({
    selectedItems,
    transferType,
    isHydrated,
    setTransferType,
    addItem,
    removeItem,
    clearSelection
  }), [selectedItems, transferType, isHydrated, addItem, removeItem, clearSelection]);

  return <TransferContext.Provider value={value}>{children}</TransferContext.Provider>;
};

export const useTransferContext = () => {
  const context = useContext(TransferContext);
  if (!context) throw new Error('useTransferContext must be used within TransferProvider');
  return context;
};
